import React, { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import { InfoText, ModalWrapper, TitleText, TitleWrapper, ButtonRow, Expandable, StyledBackdrop } from "./Styled";
import { api } from "../../../../shared/hooks/api";
import CustomTable from "../../../../shared/components/CustomTable";
import { Material } from "../../../../data/entities";
import SearchBarWithSuggestion from "../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import { sum } from "lodash";
import { StyledButton } from "../../../../shared/styled/TitleBar";
import { MaterialRow, MaterialTableColumns } from "../MaterialList";

interface AddMaterialModalProps {
    selectedMaterials: Material[];
    providerId?: string | number | null;
    providerName?: string;
    sourceMaterials?: Material[];
    open: boolean;
    handleClose: any;
    mergeMaterialOrder: Function;
}

const isSameId = (left?: string | number | null, right?: string | number | null) => {
    if (left === undefined || left === null || right === undefined || right === null) return false;
    return String(left) === String(right);
};

const getProviderRelation = (material: any, providerId?: string | number | null) => {
    if (!providerId) return undefined;
    return (material.providers || []).find((item: any) => (
        isSameId(item.provider?.id, providerId) ||
        isSameId(item.provider_id, providerId) ||
        isSameId(item.id, providerId)
    ));
};

const applySelectedProvider = (material: any, providerId?: string | number | null, providerName?: string) => {
    const providerRelation = getProviderRelation(material, providerId);

    return {
        ...material,
        provider_name: providerRelation?.provider?.name || providerName || material.provider_name,
        provider_price: providerRelation?.price ?? material.provider_price ?? 0,
    };
};

export const AddMaterialModal = ({
    selectedMaterials,
    providerId,
    providerName,
    sourceMaterials,
    open,
    handleClose,
    mergeMaterialOrder,
}: AddMaterialModalProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [{ data }, fetchMaterials] = api.get(
        `/materials?is_active=true&page_size=${Number.MAX_SAFE_INTEGER}`,
        undefined,
        true
    );
    const [selectedItems, setSelectedItems] = useState<Material[]>(
        selectedMaterials.map((item: any) => applySelectedProvider(item, providerId, providerName))
    );

    const sourceMaterialIds = new Set((sourceMaterials || []).map((material: any) => String(material.id)));
    const materials = data && data.results
        ? data.results
            .filter((item: any) => sourceMaterialIds.size === 0 || sourceMaterialIds.has(String(item.id)))
            .filter((item: any) => !!getProviderRelation(item, providerId))
            .map((item: any) => applySelectedProvider(item, providerId, providerName))
        : [];
    //const totalItems = data && data.count ? data.count : 0;
    const summaryMaterial = {
        id: `Tổng ${selectedItems.length} vật tư`,
        name: "Subtotal",
        price: sum(selectedItems.map((item: any) => (item.provider_price || 0) * (item.missing_quantity || 0))),
        isSummary: true,
    };
    const tableItems = selectedItems.length === 0 ? [] : [...selectedItems, summaryMaterial];

    const removeSelectedItem = (removedItem: Material) => {
        const removedSelectedItems = selectedItems.filter(
            (selectedItem: Material) => selectedItem.name !== removedItem.name
        );
        setSelectedItems(removedSelectedItems);
    };

    const updateItemSelectedQuantity = (id: number, selectedQuantity: number) => {
        const updatedSelectedItems = selectedItems.map((material: any) => {
            if (material.id === id) return { ...material, missing_quantity: selectedQuantity };
            return material;
        });
        setSelectedItems(updatedSelectedItems);
    };

    const handleOnclickItem = (item: Material, isAdded: boolean) => {
        const isItemContained = selectedItems.some((selectedItem) => selectedItem.name === item.name);
        if (isAdded && !isItemContained) {
            setSelectedItems([...selectedItems, applySelectedProvider(item, providerId, providerName)]);
        } else if (!isAdded && isItemContained) {
            removeSelectedItem(item);
        }
    };

    const onClickAddMaterials = () => {
        mergeMaterialOrder({ materials: selectedItems });
        handleClose();
    };

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    useEffect(() => {
        if (open) {
            setIsOpen(true);
        } else {
            const timer = setTimeout(() => setIsOpen(false), 200);
            return () => clearTimeout(timer);
        }
    }, [open]);

    return (
        <Modal
            open={open}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <ModalWrapper isOpen={isOpen}>
                <Expandable>
                    <TitleWrapper>
                        <TitleText>Thêm vật tư</TitleText>
                        <InfoText>Vui lòng lựa chọn vật tư</InfoText>
                    </TitleWrapper>

                    <SearchBarWithSuggestion
                        menuList={materials}
                        selectedItems={selectedItems}
                        renderInPortal
                        fetchItems={(variables: any) =>
                            fetchMaterials({
                                ...variables,
                                //page_size: totalItems,
                            })
                        }
                        handleOnclickItem={handleOnclickItem}
                    />

                    <CustomTable
                        itemList={tableItems}
                        RowElement={MaterialRow}
                        TableColumns={MaterialTableColumns}
                        extraRowProps={{
                            removeItem: removeSelectedItem,
                            onChangeMaterialMissingQuantity: updateItemSelectedQuantity,
                        }}
                        containerStyle={{ maxHeight: "400px" }}
                    />
                </Expandable>

                <ButtonRow>
                    <StyledButton onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                    <StyledButton variant="contained" onClick={onClickAddMaterials}>
                        Lưu vật tư
                    </StyledButton>
                </ButtonRow>
            </ModalWrapper>
        </Modal>
    );
};
